import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Workflow, WorkflowConfig } from "@/types/workflow";
import { Json } from "@/integrations/supabase/types";

// Type guard for workflow config
const parseWorkflowConfig = (config: Json | null): WorkflowConfig => {
  if (!config) return {};
  if (typeof config === 'object' && !Array.isArray(config)) {
    return config as unknown as WorkflowConfig;
  }
  return {};
};

export const useWorkflows = () => {
  return useQuery({
    queryKey: ["workflows"],
    queryFn: async () => {
      console.log("[useWorkflows] Fetching workflows");

      const { data, error } = await supabase
        .from("workflows")
        .select("*")
        .order("name", { ascending: true });

      if (error) {
        console.error("[useWorkflows] Error:", error);
        throw error;
      }

      console.log("[useWorkflows] Fetched", data?.length || 0, "workflows");
      
      return (data || []).map(workflow => ({
        ...workflow,
        workflow_config: parseWorkflowConfig(workflow.workflow_config)
      })) as Workflow[];
    },
    staleTime: 60000, // 1 minute
  });
};

export const useWorkflow = (workflowId: string | null) => {
  return useQuery({
    queryKey: ["workflow", workflowId],
    queryFn: async () => {
      if (!workflowId) return null;

      const { data, error } = await supabase
        .from("workflows")
        .select("*")
        .eq("id", workflowId)
        .single();

      if (error) {
        console.error("[useWorkflow] Error:", error);
        throw error;
      }

      return {
        ...data,
        workflow_config: parseWorkflowConfig(data.workflow_config)
      } as Workflow;
    },
    enabled: !!workflowId,
    staleTime: 30000,
  });
};
